import {
  Annotation,
  Compartment,
  EditorState,
  Prec,
  type Extension,
} from "@codemirror/state";
import { Decoration, EditorView, keymap } from "@codemirror/view";
import { useEffect, useImperativeHandle, useRef, type Ref } from "react";

import type { CodeLanguage } from "./lib/code-language";
import {
  coreExtensions,
  languageExtension,
  themeExtension,
} from "./lib/code-mirror-extensions";
import type { CodeFormatErrorRange } from "./lib/format-code";

const syncFromProp = Annotation.define<boolean>();

function formatErrorExtension(
  lines: readonly number[],
  ranges: readonly CodeFormatErrorRange[],
  doc: EditorState["doc"],
): Extension {
  const decorations = [...new Set(lines)]
    .sort((left, right) => left - right)
    .map((line) =>
      Decoration.line({
        attributes: { class: "cm-format-error-line" },
      }).range(doc.line(Math.min(Math.max(line, 1), doc.lines)).from),
    );

  for (const range of ranges) {
    const from = Math.max(0, Math.min(range.from, doc.length));
    const to = Math.max(from, Math.min(range.to, doc.length));
    let cursor = from;
    while (cursor < to) {
      const line = doc.lineAt(cursor);
      const end = Math.min(to, line.to);
      if (end > cursor) {
        decorations.push(
          Decoration.mark({ class: "cm-format-error-range" }).range(
            cursor,
            end,
          ),
        );
      }
      cursor = Math.max(end, line.to) + 1;
    }
  }

  if (decorations.length === 0) {
    return [];
  }

  decorations.sort((left, right) => left.from - right.from);
  return EditorView.decorations.of(Decoration.set(decorations));
}

export type CodeMirrorViewHandle = {
  getDoc: () => string;
};

export default function CodeMirrorView({
  value,
  onChange,
  language,
  theme,
  readOnly = false,
  ariaLabel,
  errorLines = [],
  errorRanges = [],
  onFormat,
  ref,
}: {
  readonly value: string;
  readonly onChange?: (value: string) => void;
  readonly language: CodeLanguage;
  readonly theme: "light" | "dark";
  readonly readOnly?: boolean;
  readonly ariaLabel: string;
  readonly errorLines?: readonly number[];
  readonly errorRanges?: readonly CodeFormatErrorRange[];
  readonly onFormat?: (source: string) => void;
  readonly ref?: Ref<CodeMirrorViewHandle>;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const onChangeRef = useRef(onChange);
  const onFormatRef = useRef(onFormat);
  const languageCompartment = useRef(new Compartment());
  const themeCompartment = useRef(new Compartment());
  const readOnlyCompartment = useRef(new Compartment());
  const ariaCompartment = useRef(new Compartment());
  const errorLineCompartment = useRef(new Compartment());

  onChangeRef.current = onChange;
  onFormatRef.current = onFormat;

  useImperativeHandle(ref, () => ({
    getDoc: () => viewRef.current?.state.doc.toString() ?? value,
  }));

  useEffect(() => {
    if (!parentRef.current) {
      return;
    }

    const view = new EditorView({
      parent: parentRef.current,
      state: EditorState.create({
        doc: value,
        extensions: [
          coreExtensions(),
          Prec.high(
            keymap.of([
              {
                key: "Shift-Alt-f",
                run: (view) => {
                  if (!onFormatRef.current) {
                    return false;
                  }
                  onFormatRef.current(view.state.doc.toString());
                  return true;
                },
              },
            ]),
          ),
          languageCompartment.current.of(languageExtension(language)),
          themeCompartment.current.of(themeExtension(theme, readOnly)),
          readOnlyCompartment.current.of([
            EditorView.editable.of(!readOnly),
            EditorState.readOnly.of(readOnly),
          ]),
          ariaCompartment.current.of(
            EditorView.contentAttributes.of({ "aria-label": ariaLabel }),
          ),
          errorLineCompartment.current.of(
            formatErrorExtension(
              errorLines,
              errorRanges,
              EditorState.create({ doc: value }).doc,
            ),
          ),
          EditorView.updateListener.of((update) => {
            if (!update.docChanged) {
              return;
            }
            if (
              update.transactions.some((transaction) =>
                transaction.annotation(syncFromProp),
              )
            ) {
              return;
            }
            onChangeRef.current?.(update.state.doc.toString());
          }),
        ],
      }),
    });
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // EditorView is an imperative shell; language/theme/value sync below.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) {
      return;
    }

    const current = view.state.doc.toString();
    if (current !== value) {
      view.dispatch({
        changes: { from: 0, to: current.length, insert: value },
        annotations: syncFromProp.of(true),
      });
    }
  }, [value]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) {
      return;
    }

    view.dispatch({
      effects: [
        languageCompartment.current.reconfigure(languageExtension(language)),
        themeCompartment.current.reconfigure(themeExtension(theme, readOnly)),
        readOnlyCompartment.current.reconfigure([
          EditorView.editable.of(!readOnly),
          EditorState.readOnly.of(readOnly),
        ]),
        ariaCompartment.current.reconfigure(
          EditorView.contentAttributes.of({ "aria-label": ariaLabel }),
        ),
      ],
    });
  }, [ariaLabel, language, readOnly, theme]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) {
      return;
    }

    view.dispatch({
      effects: [
        errorLineCompartment.current.reconfigure(
          formatErrorExtension(errorLines, errorRanges, view.state.doc),
        ),
      ],
    });
  }, [errorLines, errorRanges, value]);

  return <div ref={parentRef} className="size-full min-h-0" />;
}
